// @emotion/styled loaded via require() — the ES module import binding is not fully
// established when this module body executes (webpack init-order issue with Emotion's
// internal Proxy), causing ReferenceError. require() forces synchronous resolution.
/* eslint-disable @typescript-eslint/no-var-requires */
const _emotionStyled = require('@emotion/styled');
const styledBase = _emotionStyled.default || _emotionStyled;
/* eslint-enable @typescript-eslint/no-var-requires */

const SCALE_REGEX = /(-?\d+\.?\d*)px/g;

// Process a literal CSS string — replaces all Npx with calc(var(--screenScale) * Npx)
export function processString(str) {
	return str.replace(SCALE_REGEX, (_, n) =>
		parseFloat(n) === 0 ? '0' : `calc(var(--screenScale) * ${n}px)`
	);
}

// Detect Emotion SerializedStyles (result of css`...`)
const isCssObject = (expr) =>
	expr != null && typeof expr === 'object' && typeof expr.styles === 'string';

// Process an expression value — handles css() objects and function expressions
function processExpr(expr) {
	if (expr == null || typeof expr === 'number' || typeof expr === 'boolean') {
		return expr;
	}
	if (typeof expr === 'string') {
		return processString(expr);
	}
	if (isCssObject(expr)) {
		// Static css`...` result — process its .styles string directly
		return { ...expr, styles: processString(expr.styles) };
	}
	if (typeof expr === 'function') {
		// Props interpolation function — wrap to process whatever it returns at render time
		return (props) => {
			const result = expr(props);
			if (isCssObject(result)) return { ...result, styles: processString(result.styles) };
			if (typeof result === 'string') return processString(result);
			return result;
		};
	}
	// React components, plain objects, arrays — pass through unchanged
	return expr;
}

// Detect and handle ${variable}px patterns by inspecting string/expr boundaries.
// When a literal string part starts with 'px', the preceding expression is a px value.
function processTemplate(strings, exprs) {
	const resultExprs = exprs.map(processExpr);
	const resultStrings = Array.from(strings).map((str, i) => {
		if (i > 0 && /^px(\W|$)/.test(str)) {
			const prevExpr = exprs[i - 1];
			if (typeof prevExpr === 'number') {
				// Static number at definition time — inline as calc()
				resultExprs[i - 1] = `calc(var(--screenScale) * ${prevExpr}px)`;
				return processString(str.slice(2));
			}
			if (typeof prevExpr === 'function') {
				// Function that returns a number at render time — wrap to produce calc()
				resultExprs[i - 1] = (props) => {
					const val = prevExpr(props);
					return typeof val === 'number' || typeof val === 'string'
						? `calc(var(--screenScale) * ${val}px)`
						: val;
				};
				return processString(str.slice(2));
			}
			// Unknown type — leave untouched, do not corrupt output
		}
		return processString(str);
	});
	return { strings: resultStrings, raw: [...resultStrings], exprs: resultExprs };
}

function createStyledFn(baseFn) {
	return function scaledStyledFn(strings, ...exprs) {
		if (!Array.isArray(strings) || strings.raw == null) {
			// Object/function call syntax — pass through unchanged
			return baseFn(strings, ...exprs);
		}
		const { strings: s, exprs: e, raw: r } = processTemplate(strings, exprs);
		const processed = Object.assign(s, { raw: r });
		return baseFn(processed, ...e);
	};
}

function customStyled(component) {
	return createStyledFn(styledBase(component));
}

// Register a factory for every HTML tag Emotion exposes.
// Factories are lightweight closures — no styled component is created until a tag is used.
Object.keys(styledBase)
	.filter(key => typeof styledBase[key] === 'function')
	.forEach(tag => {
		customStyled[tag] = createStyledFn(styledBase[tag]);
	});

export default customStyled;
